// Recipe Module (docs/RECIPE_MODULE.md) — quick "view completed runs" entry point straight from
// the Recipe Library, without having to open a recipe's Run page (which defaults to a new-run
// form) and then separately expand its history section.
import React, { useState, useEffect } from 'react';
import recipeService from '../services/recipeService';

const fmtG = (g) => (g == null ? '—' : g >= 1000 ? `${(g / 1000).toFixed(3)} kg` : `${g.toFixed(1)} g`);

// Flattens whichever shape this recipe type stores its per-run ingredient breakdown in, into one
// common { ingredientId, ingredientName, quantityLabel } list — dough keeps flour + extras (the
// only lines the Run page's lot-usage picker was ever attached to; the pre-ferment's own nested
// split is the same extras just re-sliced, not a separate ingredient).
function getIngredientLines(recipeType, h) {
  if (recipeType === 'dough') {
    const flour = h.breakdown?.flourBreakdown || [];
    const extras = h.breakdown?.extraBreakdown || [];
    return [...flour, ...extras].map(l => ({
      ingredientId: l.ingredientId,
      ingredientName: l.ingredientName || l.label || '—',
      quantityLabel: fmtG(l.quantityG),
    }));
  }
  return (h.computedLines || []).map(l => ({
    ingredientId: l.ingredientId,
    ingredientName: l.ingredientName || '—',
    quantityLabel: l.quantityKg != null ? `${l.quantityKg} kg` : '—',
  }));
}

function groupLotUsagesByIngredient(lotUsages) {
  const map = {};
  (lotUsages || []).forEach(u => {
    if (!map[u.ingredientId]) map[u.ingredientId] = [];
    map[u.ingredientId].push(u);
  });
  return map;
}

export default function RunHistoryModal({ recipe, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const fetcher = recipe.type === 'dough'
      ? recipeService.getDoughRecipeExecutions(recipe.id)
      : recipeService.getPercentageRecipeExecutions(recipe.id);
    fetcher
      .then(data => { if (!cancelled) setHistory(Array.isArray(data) ? data : []); })
      .catch(err => { if (!cancelled) setError(err.response?.data?.message || 'Failed to load run history.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [recipe.id, recipe.type]);

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">🕐 {recipe.name} — Run History</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {error && <div className="alert alert-danger py-2">{error}</div>}
            {loading ? (
              <div className="text-center text-muted py-4"><div className="spinner-border spinner-border-sm me-2" />Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-center text-muted py-4">No runs logged yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm" style={{ fontSize: 13 }}>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 28 }}></th>
                      <th className="text-nowrap">Date</th>
                      <th className="text-nowrap">By</th>
                      <th className="text-nowrap text-end">Input</th>
                      {recipe.type === 'dough' ? (
                        <th className="text-nowrap text-end">Total Dough</th>
                      ) : (
                        <>
                          <th className="text-nowrap text-end">Yield Loss</th>
                          <th className="text-nowrap text-end">Total Batch</th>
                        </>
                      )}
                      <th className="text-nowrap">Output Lot</th>
                      <th className="text-nowrap d-none d-md-table-cell">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => {
                      const isOpen = expandedId === h.id;
                      const lines = getIngredientLines(recipe.type, h);
                      const lotMap = groupLotUsagesByIngredient(h.lotUsages);
                      return (
                        <React.Fragment key={h.id}>
                          <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isOpen ? null : h.id)}>
                            <td className="text-center text-muted">{isOpen ? '▾' : '▸'}</td>
                            <td className="text-nowrap">{new Date(h.executedAt).toLocaleString()}</td>
                            <td className="text-nowrap">{h.executedByName || '—'}</td>
                            {recipe.type === 'dough' ? (
                              <>
                                <td className="text-nowrap text-end">{h.inputValueLabel}</td>
                                <td className="text-nowrap text-end fw-semibold">{fmtG(h.breakdown?.totalDoughG)}</td>
                              </>
                            ) : (
                              <>
                                <td className="text-nowrap text-end">
                                  {h.rawInputValue != null ? `${h.rawInputValue} kg → ${h.inputValue} kg` : (h.inputValue != null ? `${h.inputValue} kg` : '—')}
                                </td>
                                <td className="text-nowrap text-end">{h.yieldLossPercent != null ? `${h.yieldLossPercent}%` : '—'}</td>
                                <td className="text-nowrap text-end fw-semibold">{h.totalBatchWeight != null ? `${h.totalBatchWeight} kg` : '—'}</td>
                              </>
                            )}
                            <td className="text-nowrap">{h.outputLotCode ? <span className="badge bg-light text-dark border">{h.outputLotCode}</span> : <span className="text-muted">—</span>}</td>
                            <td className="d-none d-md-table-cell" style={{ whiteSpace: 'normal', minWidth: 160 }}>{h.notes || <span className="text-muted">—</span>}</td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={10} className="bg-light p-0">
                                <div className="p-3">
                                  <div className="fw-semibold mb-2" style={{ fontSize: 12 }}>Ingredients Used</div>
                                  {lines.length === 0 ? (
                                    <div className="text-muted" style={{ fontSize: 12 }}>No ingredient detail recorded for this run.</div>
                                  ) : (
                                    <table className="table table-sm mb-0 bg-white">
                                      <thead>
                                        <tr>
                                          <th className="text-nowrap">Ingredient</th>
                                          <th className="text-nowrap text-end">Quantity</th>
                                          <th className="text-nowrap">Lot Code(s) Used</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lines.map((l, idx) => {
                                          const usages = lotMap[l.ingredientId] || [];
                                          return (
                                            <tr key={idx}>
                                              <td>{l.ingredientName}</td>
                                              <td className="text-nowrap text-end">{l.quantityLabel}</td>
                                              <td>
                                                {usages.length === 0 ? (
                                                  <span className="text-muted">—</span>
                                                ) : usages.map((u, i) => (
                                                  <span key={i} className="badge bg-light text-dark border me-1 mb-1">
                                                    {u.lotCode}{u.quantityKg != null ? ` (${u.quantityKg} kg)` : ''}
                                                  </span>
                                                ))}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  )}
                                  <div className="d-md-none mt-2" style={{ fontSize: 12 }}>
                                    <span className="text-muted">Notes: </span>{h.notes || <span className="text-muted">—</span>}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-outline-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
